import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { Badge, Flex, Grid, Text } from '@backstage/ui';
import useAsync from 'react-use/esm/useAsync';
import { PersonMiniCard } from './PersonMiniCard';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { HomeCard } from './HomeCard';

const START_DATE_ANNOTATION = 'datamarx.com/start-date';
const LOOKAHEAD_DAYS = 90;

function parseDateUTC(dateStr: string): Date {
  return new Date(`${dateStr}T12:00:00Z`);
}

function upcomingAnniversary(startDate: Date, today: Date): Date | null {
  const thisYear = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      startDate.getUTCMonth(),
      startDate.getUTCDate(),
      12,
    ),
  );
  if (thisYear.getTime() === startDate.getTime()) return null;
  return thisYear >= today
    ? thisYear
    : new Date(
        Date.UTC(
          today.getUTCFullYear() + 1,
          startDate.getUTCMonth(),
          startDate.getUTCDate(),
          12,
        ),
      );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

export const WorkAnniversariesCard = () => {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(async () => {
    const today = new Date();
    const todayUTC = new Date(
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12),
    );
    const cutoff = new Date(todayUTC);
    cutoff.setUTCDate(cutoff.getUTCDate() + LOOKAHEAD_DAYS);

    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        [`metadata.annotations.${START_DATE_ANNOTATION}`]:
          CATALOG_FILTER_EXISTS,
      },
      fields: [
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        `metadata.annotations.${START_DATE_ANNOTATION}`,
        'spec.profile',
      ],
    });

    return items
      .flatMap(entity => {
        const dateStr = entity.metadata.annotations?.[START_DATE_ANNOTATION];
        if (!dateStr) return [];
        const startDate = parseDateUTC(dateStr);
        const anniversary = upcomingAnniversary(startDate, todayUTC);
        if (!anniversary || anniversary > cutoff) return [];
        const years = anniversary.getUTCFullYear() - startDate.getUTCFullYear();
        const daysAway = Math.round(
          (anniversary.getTime() - todayUTC.getTime()) / 86_400_000,
        );
        return [{ entity, years, daysAway }];
      })
      .sort((a, b) => a.daysAway - b.daysAway)
      .slice(0, 10);
  }, [catalogApi]);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const anniversaries = value ?? [];

  // Card chrome and heading come from the home page widget wrapper.
  return (
    <Flex direction="column" gap="3">
      {anniversaries.length === 0 ? (
        <Text variant="body-small" color="secondary">
          No work anniversaries coming up in the next {LOOKAHEAD_DAYS} days.
        </Text>
      ) : (
        <Grid.Root className="primary-home-people-grid" gap="2">
          {anniversaries.map(({ entity, years, daysAway }) => {
            const when =
              daysAway === 0
                ? 'Today! 🎉'
                : `In ${daysAway} day${daysAway === 1 ? '' : 's'}`;
            const isMilestone = years % 5 === 0;
            return (
              <PersonMiniCard
                key={entity.metadata.name}
                entity={entity}
                line1={`${ordinal(years)} year`}
                line2={when}
                badge={
                  isMilestone ? (
                    <Badge size="small">{years}yr</Badge>
                  ) : undefined
                }
              />
            );
          })}
        </Grid.Root>
      )}
    </Flex>
  );
};

export const workAnniversariesWidget = HomePageWidgetBlueprint.make({
  name: 'work-anniversaries',
  params: {
    name: 'WorkAnniversaries',
    title: '🎂 Work Anniversaries',
    description: 'Upcoming DataMarx work anniversaries',
    components: () =>
      import('./WorkAnniversariesCard').then(m => ({
        Content: m.WorkAnniversariesCard,
      })),
    componentProps: { Renderer: HomeCard },
    layout: {
      width: { defaultColumns: 6, minColumns: 3 },
      height: { defaultRows: 7, minRows: 3 },
    },
  },
});
